// src/pages/AboutTab.jsx
import React from "react";

export default function AboutTab({ onCtaClick }) {
  return (
    <div className="space-y-10">
      {/* Page Title */}
      <div className="text-center py-4">
        <h1 className="text-3xl sm:text-4xl font-black text-white">About</h1>
        <p className="text-gray-400 text-sm mt-1">The All-in-One AI Platform for Business Automation</p>
        <p className="text-gray-500 text-xs mt-2">
          Built by <span className="text-cyan-400 font-semibold">DevX House</span> — Transforming Ideas into Digital Reality
        </p>
      </div>

      {/* Mission & Vision */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Mission */}
        <div
          className="rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] cursor-default group"
          style={{
            background: "#000000",
            border: "1px solid rgba(6,182,212,0.15)",
            boxShadow: "none"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 0 30px rgba(6,182,212,0.15)";
            e.currentTarget.style.borderColor = "rgba(6,182,212,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "rgba(6,182,212,0.15)";
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)" }}>
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white">Our Mission</h2>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            To democratize AI-powered business tools, making enterprise-grade automation accessible to
            businesses of all sizes — from solo freelancers to growing startups. We believe every business
            deserves intelligent technology that works 24/7.
          </p>
        </div>

        {/* Vision */}
        <div
          className="rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] cursor-default group"
          style={{
            background: "#000000",
            border: "1px solid rgba(168,85,247,0.15)",
            boxShadow: "none"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 0 30px rgba(168,85,247,0.15)";
            e.currentTarget.style.borderColor = "rgba(168,85,247,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "rgba(168,85,247,0.15)";
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.2)" }}>
              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white">Our Vision</h2>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            A world where AI handles repetitive business tasks so humans can focus on creativity,
            relationships, and growth. By 2027, we aim to power 1 million AI-assisted business interactions
            daily across South Asia, the Middle East, and beyond.
          </p>
        </div>
      </div>

      {/* Key Features - What We Offer */}
      <div
        className="rounded-2xl p-6 sm:p-8"
        style={{
          background: "#000000",
          border: "1px solid rgba(255,255,255,0.06)"
        }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">
          What Kronos AI Offers
        </h2>
        <p className="text-center text-gray-500 text-sm mb-8 max-w-2xl mx-auto">
          Three powerful modules working together to automate your business
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {/* AI Receptionist */}
          <FeatureCard
            title="AI Receptionist"
            color="cyan"
            items={[
              "24/7 intelligent phone answering",
              "Multilingual: English, Urdu, Hindi, Arabic",
              "Auto-scrapes your website for knowledge",
              "Books appointments & answers FAQs",
              "Natural human-like voices",
              "Works with any business type"
            ]}
            poweredBy="AWS Bedrock, Vapi, Deepgram"
          />

          {/* Social Media Hub */}
          <FeatureCard
            title="Social Media Hub"
            color="purple"
            items={[
              "AI-generated posts, images & videos",
              "Multi-platform scheduling",
              "Trending hashtag suggestions",
              "Engagement analytics dashboard",
              "Team collaboration & approvals",
              "Content calendar visualization"
            ]}
            poweredBy="Gemini, Stability AI, Kling AI"
          />

          {/* Proposal Intelligence */}
          <FeatureCard
            title="Proposal Intelligence"
            color="pink"
            items={[
              "Scrapes job listings from Upwork, Fiverr",
              "AI-generates personalized proposals",
              "Matches your skills to requirements",
              "Winning template library",
              "Proposal tracking & analytics",
              "Freelancer profile optimization"
            ]}
            poweredBy="Gemini, Groq, OpenAI"
          />
        </div>
      </div>

      {/* Google Cloud Ecosystem */}
      <div
        className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
        style={{
          background: "#000000",
          border: "1px solid rgba(66,133,244,0.15)"
        }}
      >
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(66,133,244,0.1)", border: "1px solid rgba(66,133,244,0.2)" }}>
            <svg className="w-6 h-6" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          </div>
          <div>
            <h3 className="text-white text-lg font-bold">Google Cloud Ecosystem</h3>
            <p className="text-gray-500 text-xs">Deep integration across Google's AI & Cloud platform</p>
          </div>
        </div>

        <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-4xl">
          <span className="text-white font-medium">Gemini 3.1 Pro Xtended</span> is our primary AI engine — it powers all text generation, content creation, proposal writing, and our AI receptionist's intelligence. We use <span className="text-white font-medium">Google Cloud APIs</span> and <span className="text-white font-medium">Google Search</span> for job discovery.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { name: "Gemini AI", status: "Active" },
            { name: "Cloud APIs", status: "Active" },
            { name: "Vertex AI", status: "Planned" },
            { name: "Cloud Speech", status: "Planned" },
            { name: "Cloud TTS", status: "Planned" },
            { name: "Translation", status: "Active" },
          ].map((s) => (
            <div
              key={s.name}
              className="flex flex-col items-center gap-2 rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-default"
              style={{
                background: "#000000",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: "18px 16px",
                minHeight: "80px"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = s.status === "Active" ? "rgba(16,185,129,0.4)" : "rgba(59,130,246,0.4)";
                e.currentTarget.style.boxShadow = s.status === "Active" ? "0 0 25px rgba(16,185,129,0.12)" : "0 0 25px rgba(59,130,246,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <span className="text-sm font-semibold text-white text-center">{s.name}</span>
              <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${
                s.status === "Active"
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                  : "bg-blue-500/15 text-blue-400 border border-blue-500/30"
              }`}>{s.status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Technology Stack - with gradient glow outline */}
      <div
        className="rounded-2xl p-[1px]"
        style={{
          background: "linear-gradient(135deg, rgba(6,182,212,0.4), rgba(168,85,247,0.3), rgba(59,130,246,0.4))",
          boxShadow: "0 0 30px rgba(6,182,212,0.15), 0 0 60px rgba(168,85,247,0.08)"
        }}
      >
        <div className="rounded-2xl p-6" style={{ background: "#000000" }}>
          <h2 className="text-xl font-bold text-white mb-5 text-center">Built on Enterprise-Grade Technology</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "AWS Bedrock", desc: "Claude AI" },
              { name: "Google Gemini", desc: "2.5 Flash" },
              { name: "Vapi", desc: "Voice AI" },
              { name: "Deepgram", desc: "Speech-to-Text" },
              { name: "Uplift AI", desc: "Pakistani TTS" },
              { name: "LemonSqueezy", desc: "Payments" },
              { name: "Supabase", desc: "Database" },
              { name: "React", desc: "Frontend" },
              { name: "Flask", desc: "Backend" },
            ].map((tech, i) => (
              <TechBadge key={i} name={tech.name} desc={tech.desc} />
            ))}
          </div>
        </div>
      </div>

      {/* Target Markets */}
      <div className="space-y-5">
        <h2 className="text-2xl font-bold text-white text-center">Who Uses Kronos AI?</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { title: "Healthcare", desc: "Clinics, dentists, hospitals", color: "cyan" },
            { title: "Hospitality", desc: "Hotels, restaurants, salons", color: "purple" },
            { title: "Retail", desc: "E-commerce, local shops", color: "pink" },
            { title: "Freelancers", desc: "Developers, designers, writers", color: "blue" },
          ].map((market, i) => (
            <MarketCard key={i} {...market} />
          ))}
        </div>
      </div>

      {/* Traction & Numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard value="3" label="Core Modules" color="cyan" />
        <StatCard value="4+" label="Languages Supported" color="purple" />
        <StatCard value="80+" label="Business Types" color="pink" />
        <StatCard value="24/7" label="AI Availability" color="blue" />
      </div>

      {/* About DevX House */}
      <div
        className="rounded-2xl p-6 sm:p-8"
        style={{
          background: "#000000",
          border: "1px solid rgba(255,255,255,0.06)"
        }}
      >
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">DevX House</span>
          </h2>
          <p className="text-gray-500 text-sm">The Team Behind Kronos AI</p>
        </div>

        {/* Who We Are */}
        <div className="mb-6 space-y-3">
          <h3 className="text-lg font-bold text-cyan-400">Who We Are</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            DevX House is an enterprise AI infrastructure company focused on eliminating operational friction
            for businesses worldwide. We engineer next-generation AI automation solutions that scale enterprise
            operations, deploying autonomous AI agents and intelligent data pipelines.
          </p>
          <p className="text-gray-400 text-sm leading-relaxed">
            Based in Pakistan with a global vision, we specialize in building AI-powered products for emerging
            markets — with expertise in voice AI, multilingual solutions, and enterprise automation.
          </p>
        </div>

        {/* What We Do - with gradient glow outline */}
        <div
          className="mb-6 rounded-2xl p-[1px]"
          style={{
            background: "linear-gradient(135deg, rgba(168,85,247,0.4), rgba(6,182,212,0.3), rgba(236,72,153,0.4))",
            boxShadow: "0 0 30px rgba(168,85,247,0.15), 0 0 60px rgba(6,182,212,0.08)"
          }}
        >
          <div className="rounded-2xl p-5" style={{ background: "#000000" }}>
            <h3 className="text-lg font-bold text-purple-400 mb-4">What We Do</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { title: "Enterprise AI Infrastructure", desc: "Autonomous AI agents & intelligent data pipelines" },
                { title: "Ecosystem Integration", desc: "Connect databases, CRMs & enterprise tools" },
                { title: "Cloud Orchestration", desc: "Multi-platform deployments (AWS, Azure, GCP)" },
                { title: "Voice AI & Automation", desc: "Multilingual AI receptionists & workflows" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.1)"
                  }}
                >
                  <span className="text-purple-400 text-sm mt-0.5">▸</span>
                  <div>
                    <span className="text-white font-semibold text-sm">{item.title}</span>
                    <p className="text-gray-500 text-xs mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Stats - with gradient glow outline - 2x2 on mobile */}
        <div
          className="mb-6 rounded-2xl p-[1px]"
          style={{
            background: "linear-gradient(135deg, rgba(6,182,212,0.4), rgba(168,85,247,0.3), rgba(236,72,153,0.3), rgba(59,130,246,0.4))",
            boxShadow: "0 0 30px rgba(6,182,212,0.12), 0 0 60px rgba(168,85,247,0.06)"
          }}
        >
          <div className="rounded-2xl p-4" style={{ background: "#000000" }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { value: "87x", label: "Faster Deployment", color: "cyan" },
                { value: "24/7", label: "Autonomous Ops", color: "purple" },
                { value: "Zero", label: "Vendor Lock-in", color: "pink" },
                { value: "E2E", label: "Encryption", color: "blue" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.1)"
                  }}
                >
                  <div className={`text-xl sm:text-2xl font-black ${
                    stat.color === 'cyan' ? 'text-cyan-400' :
                    stat.color === 'purple' ? 'text-purple-400' :
                    stat.color === 'pink' ? 'text-pink-400' : 'text-blue-400'
                  }`}>{stat.value}</div>
                  <div className="text-[10px] text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="flex flex-col items-center gap-3 pt-5 border-t border-white/[0.06]">
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500">
            <span>Founded: 2021</span>
            <span className="text-gray-700">|</span>
            <span>HQ: Pakistan</span>
            <span className="text-gray-700">|</span>
            <span>Serving: Global</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://devxhouse.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-xl text-white text-xs font-semibold transition-all duration-300 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, rgba(6,182,212,0.2), rgba(59,130,246,0.2))",
                border: "1px solid rgba(6,182,212,0.3)"
              }}
            >
              Visit devxhouse.com
            </a>
            <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:text-cyan-300 text-xs transition-colors">
              kronos@devxhouse.com
            </a>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
          Ready to Automate Your Business?
        </h2>
        <p className="text-gray-400 text-sm mb-6 max-w-xl mx-auto">
          Start with our free tier — no credit card required. Upgrade when you're ready to scale.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => onCtaClick && onCtaClick("register")}
            className="px-6 py-3 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, rgba(6,182,212,0.3), rgba(59,130,246,0.3))",
              border: "1px solid rgba(6,182,212,0.4)",
              boxShadow: "0 0 20px rgba(6,182,212,0.2)"
            }}
          >
            Get Started Free
          </button>
          <button
            onClick={() => onCtaClick && onCtaClick("pricing")}
            className="px-6 py-3 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-105"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.1)"
            }}
          >
            View Pricing
          </button>
        </div>
      </div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ title, color, items, poweredBy }) {
  const colors = {
    cyan: { border: "rgba(6,182,212,0.15)", hoverBorder: "rgba(6,182,212,0.3)", glow: "rgba(6,182,212,0.15)", text: "text-cyan-400" },
    purple: { border: "rgba(168,85,247,0.15)", hoverBorder: "rgba(168,85,247,0.3)", glow: "rgba(168,85,247,0.15)", text: "text-purple-400" },
    pink: { border: "rgba(236,72,153,0.15)", hoverBorder: "rgba(236,72,153,0.3)", glow: "rgba(236,72,153,0.15)", text: "text-pink-400" },
  };
  const c = colors[color];

  return (
    <div
      className="rounded-xl p-5 transition-all duration-300 hover:scale-[1.02] cursor-default"
      style={{
        background: "#000000",
        border: `1px solid ${c.border}`,
        boxShadow: "none"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 30px ${c.glow}`;
        e.currentTarget.style.borderColor = c.hoverBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = c.border;
      }}
    >
      <h3 className={`text-lg font-bold ${c.text} mb-3`}>{title}</h3>
      <ul className="text-gray-400 space-y-1.5 text-xs mb-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className={c.text}>•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div className="pt-3 border-t border-white/[0.06]">
        <span className="text-[10px] text-gray-600">{poweredBy}</span>
      </div>
    </div>
  );
}

// Tech Badge Component
function TechBadge({ name, desc }) {
  return (
    <div
      className="rounded-2xl transition-all duration-300 cursor-default hover:scale-[1.02]"
      style={{
        background: "#000000",
        border: "1px solid rgba(255,255,255,0.15)",
        padding: "14px 20px"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(6,182,212,0.4)";
        e.currentTarget.style.boxShadow = "0 0 25px rgba(6,182,212,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <span className="text-white font-semibold text-sm">{name}</span>
      <span className="text-gray-500 text-sm ml-2">{desc}</span>
    </div>
  );
}

// Market Card Component
function MarketCard({ title, desc, color }) {
  const colors = {
    cyan: { border: "rgba(6,182,212,0.15)", hoverBorder: "rgba(6,182,212,0.3)", glow: "rgba(6,182,212,0.15)", text: "text-cyan-400" },
    purple: { border: "rgba(168,85,247,0.15)", hoverBorder: "rgba(168,85,247,0.3)", glow: "rgba(168,85,247,0.15)", text: "text-purple-400" },
    pink: { border: "rgba(236,72,153,0.15)", hoverBorder: "rgba(236,72,153,0.3)", glow: "rgba(236,72,153,0.15)", text: "text-pink-400" },
    blue: { border: "rgba(59,130,246,0.15)", hoverBorder: "rgba(59,130,246,0.3)", glow: "rgba(59,130,246,0.15)", text: "text-blue-400" },
  };
  const c = colors[color];

  return (
    <div
      className="rounded-xl p-5 text-center transition-all duration-300 hover:scale-[1.02] cursor-default"
      style={{
        background: "#000000",
        border: `1px solid ${c.border}`,
        boxShadow: "none"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 25px ${c.glow}`;
        e.currentTarget.style.borderColor = c.hoverBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = c.border;
      }}
    >
      <h3 className={`text-base font-bold ${c.text}`}>{title}</h3>
      <p className="text-gray-500 text-xs mt-1">{desc}</p>
    </div>
  );
}

// Stat Card Component
function StatCard({ value, label, color }) {
  const colors = {
    cyan: { border: "rgba(6,182,212,0.15)", hoverBorder: "rgba(6,182,212,0.3)", glow: "rgba(6,182,212,0.15)", text: "text-cyan-400" },
    purple: { border: "rgba(168,85,247,0.15)", hoverBorder: "rgba(168,85,247,0.3)", glow: "rgba(168,85,247,0.15)", text: "text-purple-400" },
    pink: { border: "rgba(236,72,153,0.15)", hoverBorder: "rgba(236,72,153,0.3)", glow: "rgba(236,72,153,0.15)", text: "text-pink-400" },
    blue: { border: "rgba(59,130,246,0.15)", hoverBorder: "rgba(59,130,246,0.3)", glow: "rgba(59,130,246,0.15)", text: "text-blue-400" },
  };
  const c = colors[color];

  return (
    <div
      className="rounded-xl p-5 text-center transition-all duration-300 hover:scale-[1.02] cursor-default"
      style={{
        background: "#000000",
        border: `1px solid ${c.border}`,
        boxShadow: "none"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 25px ${c.glow}`;
        e.currentTarget.style.borderColor = c.hoverBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = c.border;
      }}
    >
      <div className={`text-3xl sm:text-4xl font-black ${c.text}`}>{value}</div>
      <div className="text-gray-500 text-xs mt-1">{label}</div>
    </div>
  );
}
